import { useSyncExternalStore } from "react";

/**
 * As escolhas que não passam pelo servidor.
 *
 * Duas moram aqui, e as duas por motivos diferentes de qualquer coisa que a
 * conta guarde:
 *
 * O **relatório de erros** vale antes de existir sessão. Ele é ligado em
 * `src/main.tsx`, antes de a árvore montar, e quem joga sozinho nunca vai ter
 * conta nenhuma para carregar preferência. Perguntar ao servidor o que fazer
 * com um dado que não deveria sair do aparelho seria ao contrário.
 *
 * Tudo em try/catch, como em `online/push.ts` e `i18n/preferencia.ts`: janela
 * anônima, dados do site bloqueados e WebView com armazenamento desligado são
 * três situações reais. Sem lugar para guardar, a escolha vale até fechar a
 * aba, e nada quebra.
 *
 * As mudanças são avisadas a quem estiver olhando. Sem isso, desligar o envio
 * automático numa tela deixaria o gatilho montado em `App` continuar subindo a
 * carreira até alguém recarregar o jogo, que é o tipo de defeito que faz a
 * pessoa desconfiar do botão inteiro.
 */

const CHAVES = {
  relatorioDeErros: "lm:opcoes:relatorio",
  salvamentoAutomatico: "lm:carreira:salvamento",
  somDoJogo: "lm:opcoes:som",
} as const;

export type Preferencia = keyof typeof CHAVES;

/**
 * O valor de quem nunca mexeu em nada.
 *
 * O relatório nasce **desligado** porque `docs/LEGAL.md` promete que nada sai
 * daqui sem o sim de quem joga, e fala em "se você ativar o relatório
 * automático de erros". Promessa escrita vale mais do que a conveniência de
 * receber mais relatórios: quem quiser ajudar liga aqui.
 */
const PADROES: Record<Preferencia, boolean> = {
  relatorioDeErros: false,
  // Ligado: é o que o jogo sempre fez, e ninguém perde carreira sem ter
  // escolhido perder.
  salvamentoAutomatico: true,
  somDoJogo: true,
};

/**
 * O valor lido fica na memória.
 *
 * `useSyncExternalStore` chama a leitura a cada desenho, e ir ao
 * `localStorage` toda vez seria trabalho síncrono repetido à toa. A cópia
 * também é o que mantém a escolha de pé quando não há onde gravar.
 */
const lembradas = new Map<Preferencia, boolean>();

const ouvintes = new Set<() => void>();

export function preferenciaLigada(nome: Preferencia): boolean {
  const lembrada = lembradas.get(nome);
  if (lembrada !== undefined) return lembrada;

  let valor = PADROES[nome];
  try {
    const bruto = localStorage.getItem(CHAVES[nome]);
    if (bruto === "sim") valor = true;
    else if (bruto === "nao") valor = false;
  } catch {
    // Sem leitura possível, vale o padrão.
  }
  lembradas.set(nome, valor);
  return valor;
}

export function definirPreferencia(nome: Preferencia, ligada: boolean): void {
  lembradas.set(nome, ligada);
  try {
    localStorage.setItem(CHAVES[nome], ligada ? "sim" : "nao");
  } catch {
    // Sem lugar para guardar. A escolha vale até fechar a aba, e pronto.
  }
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(avisar: () => void): () => void {
  ouvintes.add(avisar);
  return () => {
    ouvintes.delete(avisar);
  };
}

/** A preferência, acompanhando quem a trocar em qualquer outra tela. */
export function usePreferencia(nome: Preferencia): boolean {
  return useSyncExternalStore(
    assinar,
    () => preferenciaLigada(nome),
    // Fora do navegador não existe escolha guardada, e o padrão é o que o
    // primeiro desenho precisa casar com o do cliente.
    () => PADROES[nome],
  );
}
